import { useState } from 'react';
import { BooleanInput, Create, required, SimpleForm, TextInput, useNotify, useRefresh, SaveButton, Toolbar } from 'react-admin';
import { CustomTitle } from '../../components/CustomTitle';
import { ISession } from '../../types';
import { SpeakerInput } from './SpeakerInput';
import { DatesField, RichText, LocationField, PathssField, TagssField } from './fields';
import { validateForm } from './validate';

const defaultValues: Partial<ISession> = {
  title: '',
  details: '',
  isSponsored: false,
  start: '08:00',
  end: '10:00',
};

// Custom toolbar with Save + Add button
const CustomToolbar = ({ onSaveAndAdd, originalOnSuccess }: { onSaveAndAdd: () => void, originalOnSuccess: (data: ISession) => void }) => {
  return (
    <Toolbar>
      {/* <SaveButton /> */}
      <SaveButton 
        label="Save + Add" 
        type="button"
        mutationOptions={{
          onSuccess: (args: unknown) => {
            console.log('---customToolbar.onSuccess', { args });
            onSaveAndAdd();
            window.scrollTo(0, 0);
            originalOnSuccess(args as ISession);
            console.log('---customToolbar.onSuccess 2');
          }
        }}
        variant="outlined"
        color="secondary"
      />
    </Toolbar>
  );
};

export const SessionCreate = () => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [initialValues, setInitialValues] = useState<Partial<ISession>>(defaultValues);
  const [key, setKey] = useState(0); // Key to force re-render of form
  const [isSaveAndAdd, setIsSaveAndAdd] = useState(false); // Track if Save + Add was clicked
  
  // Remember checkboxes state - separate for date, start, end
  const [rememberFields, setRememberFields] = useState({
    title: false,
    dateId: true, // Default to true since we want to preserve date
    start: false,
    end: false,
    locationsId: false,
    tagsId: false,
    pathsId: false,
    speakersId: false,
    details: false,
    isSponsored: false,
  });

  const onSuccess = (data: ISession) => {
    console.log('---- onSuccess', {isSaveAndAdd});
    notify('ra.notification.created', { type: 'success', messageArgs: { smart_count: 1 } });
    refresh();
    
    if (isSaveAndAdd || true) {
      // Reset fields based on remember checkboxes
      const newInitialValues: Partial<ISession> = {};
      
      // For each field, check if it should be preserved
      Object.keys(rememberFields).forEach(fieldKey => {
        const field = fieldKey as keyof ISession;
        if (rememberFields[fieldKey as keyof typeof rememberFields]) {
          console.log('---onSuccess remember', field, rememberFields[fieldKey as keyof typeof rememberFields]);
          // Preserve this field - use the saved data
          const value = data[field];
          if (field === 'start' || field === 'end' || field === 'title' || field === 'details' || field === 'dateId') {
            newInitialValues[field] = (typeof value === 'string' ? value : '') || '';
          } else if (field === 'isSponsored') {
            newInitialValues[field] = Boolean(value);
          } else if (field === 'locationsId' || field === 'tagsId' || field === 'pathsId' || field === 'speakersId') {
            newInitialValues[field] = Array.isArray(value) ? value : [];
          }
        } else {
          // Reset this field to default
          console.log('---onSuccess reset', field, rememberFields[fieldKey as keyof typeof rememberFields]);
          if (field === 'start') newInitialValues[field] = '08:00';
          else if (field === 'end') newInitialValues[field] = '10:00';
          else if (field === 'isSponsored') newInitialValues[field] = false;
          else if (field === 'title' || field === 'details') newInitialValues[field] = '';
          else if (field === 'dateId') newInitialValues[field] = undefined; // Reset date if not remembered
          else if (field === 'locationsId' || field === 'tagsId' || field === 'pathsId' || field === 'speakersId') {
            newInitialValues[field] = [];
          }
        }
      });
      
      setInitialValues(newInitialValues);
      setIsSaveAndAdd(false);
      
      // Force complete form re-render by changing the key
      setKey(prev => prev + 1);
    }
    console.log('---onSuccess 2');
  };

  const handleSaveAndAdd = () => {
    console.log('---handleSaveAndAdd');
    setIsSaveAndAdd(true);
    console.log('---handleSaveAndAdd 2');
  };

  const handleRememberChange = (field: keyof typeof rememberFields) => {
    console.log('---handleRememberChange', field);
    setRememberFields(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  return (
    <Create mutationOptions={{ onSuccess }} redirect={false} title={<CustomTitle />}>
      <SimpleForm 
        key={key}
        defaultValues={initialValues} 
        validate={validateForm}
        toolbar={<CustomToolbar onSaveAndAdd={handleSaveAndAdd} originalOnSuccess={onSuccess} />}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <TextInput source="title" fullWidth validate={[required()]} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input 
              type="checkbox" 
              checked={rememberFields.title}
              onChange={() => handleRememberChange('title')}
            />
            Remember
          </label>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <DatesField key={`dates-${key}`} />
        </div>
        
        {/* Separate remember checkboxes for date, start, end */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginLeft: '20px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input 
              type="checkbox" 
              checked={rememberFields.dateId}
              onChange={() => handleRememberChange('dateId')}
            />
            Remember Date
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input 
              type="checkbox" 
              checked={rememberFields.start}
              onChange={() => handleRememberChange('start')}
            />
            Remember Start Time
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input 
              type="checkbox" 
              checked={rememberFields.end}
              onChange={() => handleRememberChange('end')}
            />
            Remember End Time
          </label>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <LocationField key={`location-${key}`} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input 
              type="checkbox" 
              checked={rememberFields.locationsId}
              onChange={() => handleRememberChange('locationsId')}
            />
            Remember
          </label>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <SpeakerInput source="speakersId" key={`speakers-${key}`} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input 
              type="checkbox" 
              checked={rememberFields.speakersId}
              onChange={() => handleRememberChange('speakersId')}
            />
            Remember
          </label>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <TagssField key={`tags-${key}`} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input 
              type="checkbox" 
              checked={rememberFields.tagsId}
              onChange={() => handleRememberChange('tagsId')}
            />
            Remember
          </label>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <PathssField key={`paths-${key}`} />
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input 
              type="checkbox" 
              checked={rememberFields.pathsId}
              onChange={() => handleRememberChange('pathsId')}
            />
            Remember
          </label>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <RichText source="details" />
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input 
              type="checkbox" 
              checked={rememberFields.details}
              onChange={() => handleRememberChange('details')}
            />
            Remember
          </label>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <BooleanInput source="isSponsored" />
          <label style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <input 
              type="checkbox" 
              checked={rememberFields.isSponsored}
              onChange={() => handleRememberChange('isSponsored')}
            />
            Remember
          </label>
        </div>
      </SimpleForm>
    </Create>
  );
};
